import { useMemo } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ModeBadge from "@/components/ModeBadge";
import StrandBadge from "@/components/StrandBadge";
import {
  ArrowLeft,
  TrendingUp,
  Users,
  BarChart3,
  Award,
  Target,
  ChevronRight,
  CheckCircle2,
  Clock,
  FileAudio,
  Loader2,
  Trophy,
  Star,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAssessments, useCurrentUser, useCohorts } from "@/lib/api";
import { getScoreLabel, getScoreColor, getScoreBgColor } from "@/lib/scoring";

interface CohortStats {
  id: string;
  name: string;
  totalStudents: number;
  assessmentsCompleted: number;
  assessmentsPending: number;
  averageScore: number | null;
}

interface StrandStats {
  name: string;
  label: string;
  emoji: string;
  avgScore: number;
  completedCount: number;
}

export default function ProgressPage({ role = "student" }: { role?: "student" | "teacher" | "parent" }) {
  const { data: user } = useCurrentUser();
  const { data: assessments, isLoading: loadingAssessments } = useAssessments();
  const { data: cohorts, isLoading: loadingCohorts } = useCohorts();

  const isTeacher = role === "teacher";

  const cohortStats: CohortStats[] = useMemo(() => {
    if (!cohorts || !assessments) return [];

    return cohorts.map((cohort) => {
      const cohortAssessments = assessments.filter((a) => a.cohort_id === cohort.id);
      const completed = cohortAssessments.filter((a) => a.status === "signed_off");
      const pending = cohortAssessments.filter((a) => a.status === "draft_ready" || a.status === "under_review");

      return {
        id: cohort.id,
        name: cohort.name,
        totalStudents: cohort.student_count || cohort.students_list?.length || 0,
        assessmentsCompleted: completed.length,
        assessmentsPending: pending.length,
        averageScore: null, // No per-cohort score aggregation yet, show empty state instead of a placeholder
      };
    });
  }, [cohorts, assessments]);

  const overallStats = useMemo(() => {
    if (!assessments || !Array.isArray(assessments)) {
      return { total: 0, completed: 0, pending: 0, signedOff: 0 };
    }

    const completed = assessments.filter((a) => a.status === "signed_off");
    const pending = assessments.filter((a) => a.status === "draft_ready" || a.status === "under_review");

    return {
      total: assessments.length,
      completed: completed.length,
      pending: pending.length,
      signedOff: completed.length,
    };
  }, [assessments]);

  const strandStats: StrandStats[] = useMemo(() => {
    if (!assessments || !Array.isArray(assessments)) {
      return [
        { name: "physical", label: "Physical", emoji: "🗣️", avgScore: 0, completedCount: 0 },
        { name: "linguistic", label: "Linguistic", emoji: "📝", avgScore: 0, completedCount: 0 },
        { name: "cognitive", label: "Cognitive", emoji: "🧠", avgScore: 0, completedCount: 0 },
        { name: "social-emotional", label: "Social-Emotional", emoji: "💬", avgScore: 0, completedCount: 0 },
      ];
    }

    const completed = assessments.filter((a) => a.status === "signed_off");
    const presentingCount = completed.filter((a) => a.mode === "presenting").length;
    const explainingCount = completed.filter((a) => a.mode === "explaining").length;
    const persuadingCount = completed.filter((a) => a.mode === "persuading").length;

    return [
      { name: "physical", label: "Physical", emoji: "🗣️", avgScore: 0, completedCount: presentingCount },
      { name: "linguistic", label: "Linguistic", emoji: "📝", avgScore: 0, completedCount: completed.length },
      { name: "cognitive", label: "Cognitive", emoji: "🧠", avgScore: 0, completedCount: explainingCount },
      { name: "social-emotional", label: "Social-Emotional", emoji: "💬", avgScore: 0, completedCount: persuadingCount },
    ];
  }, [assessments]);

  if (isTeacher) {
    return (
      <AppLayout role="teacher">
        <div className="space-y-6">
          <Link
            to="/teacher"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl font-bold text-foreground">Cohort Progress Dashboard</h1>
            <p className="text-sm text-muted-foreground">Overview of all your cohorts and their progress.</p>
          </motion.div>

          {/* Overall Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{cohortStats.length}</p>
                    <p className="text-xs text-muted-foreground">Total Cohorts</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{overallStats.signedOff}</p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{overallStats.pending}</p>
                    <p className="text-xs text-muted-foreground">Pending Review</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{overallStats.total}</p>
                    <p className="text-xs text-muted-foreground">Total Assessments</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Strand Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Skills Progress Overview
              </CardTitle>
              <CardDescription>Assessment completion across different skill strands</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {strandStats.map((strand, idx) => (
                  <motion.div
                    key={strand.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + idx * 0.05 }}
                  >
                    <Card className="bg-gradient-to-br from-secondary/50 to-secondary/30">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-2xl">{strand.emoji}</span>
                          <div>
                            <p className="font-medium text-foreground">{strand.label}</p>
                            <p className="text-xs text-muted-foreground">Strand</p>
                          </div>
                        </div>
                        {strand.name !== "physical" ? (
                          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                            Coming Soon
                          </div>
                        ) : (
                          <>
                            <div className="flex items-end justify-between">
                              <div>
                                <p className="text-2xl font-bold text-primary">{strand.completedCount}</p>
                                <p className="text-xs text-muted-foreground">completed</p>
                              </div>
                              <div className="text-right">
                                <p className={`text-lg font-semibold ${getScoreColor(strand.avgScore)}`}>{strand.avgScore > 0 ? strand.avgScore : "N/A"}</p>
                                <p className={`text-xs ${strand.avgScore > 0 ? getScoreColor(strand.avgScore) : "text-muted-foreground"}`}>{strand.avgScore > 0 ? getScoreLabel(strand.avgScore) : "No data yet"}</p>
                              </div>
                            </div>
                            <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${getScoreBgColor(strand.avgScore)}`}
                                style={{ width: `${(strand.avgScore / 5) * 100}%` }}
                              />
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Cohort Cards */}
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">Cohort Performance</h2>
            {loadingCohorts || loadingAssessments ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : cohortStats.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="font-medium text-foreground">No cohorts found</p>
                  <p className="text-sm text-muted-foreground">Create a cohort to see progress data.</p>
                  <Link to="/teacher/students">
                    <Button variant="outline" className="mt-4">Manage Students</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cohortStats.map((cohort, idx) => (
                  <motion.div
                    key={cohort.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + idx * 0.05 }}
                  >
                    <Card className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">{cohort.name}</CardTitle>
                          <Badge variant="secondary">{cohort.totalStudents} students</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground flex items-center gap-1">
                              <CheckCircle2 className="w-4 h-4 text-green-500" /> Completed
                            </span>
                            <span className="font-medium">{cohort.assessmentsCompleted}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Clock className="w-4 h-4 text-yellow-500" /> Pending
                            </span>
                            <span className="font-medium">{cohort.assessmentsPending}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-500" /> Avg score
                            </span>
                            <span className={cohort.averageScore != null ? "font-medium" : "text-muted-foreground"}>
                              {cohort.averageScore != null ? cohort.averageScore : "N/A"}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </AppLayout>
    );
  }

  // Student/Parent view - improved with scoring data
  return (
    <StudentProgressView role={role} />
  );
}

function StudentProgressView({ role = "student" }: { role?: "student" | "parent" }) {
  const { data: assessments, isLoading } = useAssessments();

  const { portfolio, stats, strandScores, recentAssessments, completedCount } = useMemo(() => {
    const defaultPortfolio: { id: string; title: string; date: string; mode: string; strand: string; status: string; score: number; }[] = [];
    const defaultStats = { total: 0, completed: 0, inProgress: 0 };

    if (!assessments || !Array.isArray(assessments)) {
      return { 
        portfolio: defaultPortfolio, 
        stats: defaultStats, 
        completedCount: 0,
        strandScores: { physical: 0, linguistic: 0, cognitive: 0, social_emotional: 0 },
        modeBreakdown: { presenting: 0, explaining: 0, persuading: 0 },
        recentAssessments: [],
      };
    }

    const completed = assessments.filter((a) => a.status === "signed_off");
    // Match the dashboard's in-progress definition (StudentDashboard) so the count
    // is consistent across pages — an assessment that's uploading or processing is
    // in progress too, not just draft_ready/under_review.
    const inProgress = assessments.filter((a) => ["uploading", "processing", "draft_ready", "under_review"].includes(a.status));
    const completedCount = completed.length;

    // Calculate mode breakdown
    const modes = { presenting: 0, explaining: 0, persuading: 0 };
    completed.forEach((a) => {
      if (a.mode === "presenting") modes.presenting++;
      else if (a.mode === "explaining") modes.explaining++;
      else if (a.mode === "persuading") modes.persuading++;
    });

    // Strand scores require per-assessment signed report fetches; show no-data
    // until the progress page fetches and aggregates real signed report scores.
    const strandScores = {
      physical: 0,
      linguistic: 0,
      cognitive: 0,
      social_emotional: 0,
    };

    const items = completed
      .sort((a, b) => new Date(b.completed_at || b.created_at || 0).getTime() - new Date(a.completed_at || a.created_at || 0).getTime())
      .map((a) => {
        const date = a.completed_at || a.created_at;
        const modeToStrand: Record<string, string> = { presenting: "physical", explaining: "cognitive", persuading: "social-emotional" };
        return {
          id: a.id,
          title: a.prompt_name || `${a.mode.charAt(0).toUpperCase() + a.mode.slice(1)} Assessment`,
          date: date ? new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "N/A",
          mode: a.mode,
          strand: modeToStrand[a.mode] || "physical",
          status: a.status,
          score: 0,
        };
      });

    return {
      portfolio: items,
      stats: { total: assessments.length, completed: completedCount, inProgress: inProgress.length },
      completedCount,
      strandScores,
      modeBreakdown: modes,
      recentAssessments: items.slice(0, 10),
    };
  }, [assessments]);

  return (
    <AppLayout role={role}>
      <div className="space-y-6">
        <Link
          to={role === "student" ? "/student" : "/parent"}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold text-foreground mb-1">My Progress</h1>
          <p className="text-sm text-muted-foreground">Track your oracy skills growth across different strands.</p>
        </motion.div>

        {/* Overall Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200">
              <CardContent className="flex flex-col items-center gap-1 p-4">
                <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">{stats.total}</div>
                <p className="text-xs text-blue-600/70">Total Tasks</p>
              </CardContent>
            </Card>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200">
              <CardContent className="flex flex-col items-center gap-1 p-4">
                <div className="text-3xl font-bold text-green-700 dark:text-green-300">{stats.completed}</div>
                <p className="text-xs text-green-600/70">Completed</p>
              </CardContent>
            </Card>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-950 dark:to-yellow-900 border-yellow-200">
              <CardContent className="flex flex-col items-center gap-1 p-4">
                <div className="text-3xl font-bold text-yellow-700 dark:text-yellow-300">{stats.inProgress}</div>
                <p className="text-xs text-yellow-600/70">In Progress</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Strand Scores */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Strand Scores
              </CardTitle>
              <CardDescription>Your performance across different oracy skill areas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: "physical", label: "Physical", emoji: "🗣️", score: strandScores.physical },
                  { key: "linguistic", label: "Linguistic", emoji: "📝", score: strandScores.linguistic },
                  { key: "cognitive", label: "Cognitive", emoji: "🧠", score: strandScores.cognitive },
                  { key: "social_emotional", label: "Social-Emotional", emoji: "💬", score: strandScores.social_emotional },
                ].map((strand) => {
                  const isPhysical = strand.key === "physical";
                  const clickable = isPhysical && role === "student";
                  const inner = (
                    <div className={`flex flex-col items-center p-4 rounded-xl bg-secondary/30 ${clickable ? "hover:bg-secondary/50 transition-colors cursor-pointer" : ""}`}>
                      <span className="text-3xl mb-2">{strand.emoji}</span>
                      <p className="font-medium text-sm mb-1">{strand.label}</p>
                      {!isPhysical ? (
                        <div className="py-4 text-sm text-muted-foreground">Coming Soon</div>
                      ) : strand.score > 0 ? (
                        <>
                          <p className={`text-3xl font-bold ${getScoreColor(strand.score)}`}>
                            {strand.score}
                          </p>
                          <p className={`text-xs mt-1 ${getScoreColor(strand.score)}`}>
                            {getScoreLabel(strand.score)}
                          </p>
                          <div className="w-full mt-3 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${getScoreBgColor(strand.score)}`}
                              style={{ width: `${(strand.score / 5) * 100}%` }}
                            />
                          </div>
                          {clickable && (
                            <span className="mt-3 flex items-center gap-1 text-xs font-medium text-primary">
                              View assessments <ChevronRight className="w-3 h-3" />
                            </span>
                          )}
                        </>
                      ) : (
                        <div className="py-2 flex flex-col items-center">
                          <p className="text-sm text-muted-foreground">No data yet</p>
                          {clickable && (
                            <span className="mt-2 flex items-center gap-1 text-xs font-medium text-primary">
                              Start an assessment <ChevronRight className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  return clickable ? (
                    <Link key={strand.key} to="/student/tasks">{inner}</Link>
                  ) : (
                    <div key={strand.key}>{inner}</div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>


        {/* Recent Assessments with Scores */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Recent Assessments
              </CardTitle>
              <CardDescription>Your latest completed assessments with scores</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : recentAssessments.length === 0 ? (
                <div className="text-center py-8">
                  <FileAudio className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="font-medium text-foreground">No completed assessments yet</p>
                  <p className="text-sm text-muted-foreground">Complete your first assessment to see your progress.</p>
                  <Link to="/student/tasks">
                    <Button className="mt-4">Start Assessment</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentAssessments.map((assessment) => (
                    <Link key={assessment.id} to={`/student/feedback/${assessment.id}`}>
                      <div className="flex items-center gap-4 p-4 rounded-lg border hover:bg-secondary/50 transition-colors">
                        <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center shrink-0">
                          <Award className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{assessment.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <ModeBadge mode={assessment.mode === "presenting" ? "presentational" : assessment.mode === "explaining" ? "exploratory" : "collaborative"} className="text-[10px]" />
                            <span className="text-xs text-muted-foreground">{assessment.date}</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`text-xl font-bold ${getScoreColor(assessment.score)}`}>
                            {assessment.score > 0 ? assessment.score : "—"}
                          </p>
                          <p className={`text-xs ${assessment.score > 0 ? getScoreColor(assessment.score) : "text-muted-foreground"}`}>
                            {assessment.score > 0 ? getScoreLabel(assessment.score) : "score"}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Milestone / Next Target */}
        {completedCount > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
            <Card className="bg-gradient-to-r from-amber-500 to-orange-500 border-none text-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <Trophy className="w-8 h-8" />
                  <div>
                    <p className="font-semibold">Keep up the great work!</p>
                    <p className="text-sm text-white/80">
                      You've completed {completedCount} assessment{completedCount !== 1 ? "s" : ""}. 
                      Continue practicing to improve your scores across all strands!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </AppLayout>
  );
}
